import { useState, useMemo, useEffect } from 'react';
import * as formik from 'formik';
import * as yup from 'yup';
import { Table, Form, Button, InputGroup, Row, Col, Card, Badge, Stack, OverlayTrigger, Tooltip, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getDiscoverForm, getTransactionMatches } from '../utils/api';
import Header from './Header';
import { useDataStore } from '../Store';
import Loader from './Loader';
import { faCar, faCirclePlus, faCircleXmark, faTrashCan, faX } from '@fortawesome/free-solid-svg-icons';
import SeparatorLine from './SeparatorLine';
import { formatCurrency, formatDateToMMDDYYYY } from '../utils/helpers';
import InformationTooltip from './InformationTooltip';
import Error from './Error';
import DataSelectionModal from './Modals/DataSelectionModal';
import CreateEditTransactionModal from './Modals/CreateEditTransactionModal';
import Assets from './Assets';

const Discover = () => {

  const selectedDealer = useDataStore((state) => state.selectedDealer);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    const data = await getDiscoverForm();
    if (data) {
      setData(data);
    }
    else {
      setError('Failed to fetch data');
    }
    setLoading(false);
  };
  useEffect(() => {
    fetchData();
  }, [selectedDealer]);


  const [data, setData] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [match, setMatch] = useState({});
  const [showCreateTransactionModal, setShowCreateTransactionModal] = useState(false);

  const [showDataSelectionModal, setShowDataSelectionModal] = useState(false);
  const [selectionModalChild, setSelectionModalChild] = useState(null);

  const { Formik } = formik;

  const schema = yup.object().shape({
    state: yup.number().required('This field is required'),
    newAssets: yup.array().min(1, 'At least one is required').of(
      yup.object({
        newAssetId: yup.string().required('This field is required'),
        newAssetVin: yup.string().required('This field is required'),
        newAssetAdjustedCapCost: yup.number().required('This field is required')
      })
    ),
    retiredAssets: yup.array().of(
      yup.object({
        retiredAssetId: yup.string().required('This field is required'),
        retiredAssetVin: yup.string().required('This field is required'),
        retiredAssetAdjustedCapCost: yup.number().required('This field is required')
      })
    )
  });


  const handleCreateTransaction = async (match) => {
    setMatch(match);
    setShowCreateTransactionModal(true);
  }

  const handleCheckboxChange = async (asset) => {

  }

  return (
    <div style={{ padding: 10 }}>
      <>
        <Header title='Discover' description="Search for comparable Assets" />
        {loading ? <Loader /> : (
          <>
            <Formik
              validationSchema={schema}
              onSubmit={async (form) => {
                setResultsLoading(true);
                const res = await getTransactionMatches(selectedDealer, form)
                if (res) {
                  setMatches(res)
                  setError(false);
                }
                else {
                  setMatches([])
                  setError(true);
                }
                setResultsLoading(false);
              }}
              initialValues={{
                state: '',

                rule: {
                  Id: '',
                  State: {
                    Name: ''
                  },
                  Description: '',
                },
                newAssets: [],
                retiredAssets: [],
              }}
            >
              {({ handleSubmit, handleChange, values, touched, errors, setFieldValue, setFieldTouched }) => (
                <Form noValidate onSubmit={handleSubmit}>
                  <Row>
                    <Col xs={12} sm={12} md={12} lg={12}>
                      <InputGroup>
                        <Form.Select id='state' name='state' value={values.state} onChange={(e) => {
                          const selectedState = e.target.value;
                          setFieldValue('state', selectedState)
                          setFieldTouched({ ...touched, state: true });
                          setFieldValue('rule', data?.Rules?.find(c => c.State.Id.toString() === selectedState));
                        }}
                          isInvalid={touched.state && !!errors.state} isValid={values.state && !errors.state} aria-label="State" >
                          <option value="">Select a state</option>

                          {data?.States?.map((o, index) => (
                            <option key={o.Id} value={o.Id}>{o.Name}</option>
                          ))}
                        </Form.Select>
                        {touched.state && errors.state && (<Form.Control.Feedback type='invalid'>{errors.state}</Form.Control.Feedback>)}
                      </InputGroup>
                      <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="State"><span style={{ color: 'red' }}>*</span>State</Form.Label>
                    </Col>
                  </Row>
                  {values.rule && (
                    <>
                      <Row>
                        <Col xs={6} sm={6} md={2} lg={2}>
                          <Form.Group>
                            <Form.Control id="ruleId" value={values.rule?.Id} onChange={handleChange} isInvalid={errors.rule?.Id} isValid={values.rule?.Id && !errors.rule?.Id} disabled aria-label="Rule Id" />
                            <Form.Control.Feedback type='invalid'>{errors.rule?.Id}</Form.Control.Feedback>
                          </Form.Group>
                          <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="ruleId">Rule Id</Form.Label>
                        </Col>
                        <Col xs={6} sm={6} md={2} lg={2}>
                          <Form.Group>
                            <Form.Control id="ruleState" value={values.rule?.State?.Name} onChange={handleChange} isInvalid={errors.rule?.State?.Name} isValid={values.rule?.State?.Name && !errors.rule?.State?.Name} disabled aria-label="Rule State" />
                            <Form.Control.Feedback type='invalid'>{errors.rule?.State?.Name}</Form.Control.Feedback>
                          </Form.Group>
                          <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="ruleState">State</Form.Label>
                        </Col>
                        <Col xs={12} sm={6} md={4} lg={4}>
                          <Form.Group>
                            <Form.Control id="ruleDescription" value={values.rule?.Description} onChange={handleChange} isInvalid={errors.rule?.Description} isValid={values.rule?.Description && !errors.rule?.Description} disabled aria-label="Rule Description" />
                            <Form.Control.Feedback type='invalid'>{errors.rule?.Description}</Form.Control.Feedback>
                          </Form.Group>
                          <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="ruleDescription">Description</Form.Label>
                        </Col>
                        <Col xs={6} sm={6} md={2} lg={2}>
                          <Form.Group>
                            <Form.Control id="ruleMaxPurchaseQty" value={values.rule?.MaxPurchaseQty} onChange={handleChange} isInvalid={errors.rule?.MaxPurchaseQty} isValid={values.rule?.MaxPurchaseQty && !errors.rule?.MaxPurchaseQty} disabled aria-label="Rule Max Purchase Qty" />
                            <Form.Control.Feedback type='invalid'>{errors.rule?.MaxPurchaseQty}</Form.Control.Feedback>
                          </Form.Group>
                          <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="ruleMaxPurchaseQty">Max Purchase Qty</Form.Label>
                        </Col>
                        <Col xs={6} sm={6} md={2} lg={2}>
                          <Form.Group>
                            <Form.Control id="ruleMaxSaleQty" value={values.rule?.MaxSaleQty ?? "N/A"} onChange={handleChange} isInvalid={errors.rule?.MaxSaleQty} isValid={values.rule?.MaxSaleQty && !errors.rule?.MaxSaleQty} disabled aria-label="Rule Max Sale Qty" />
                            <Form.Control.Feedback type='invalid'>{errors.rule?.MaxSaleQty}</Form.Control.Feedback>
                          </Form.Group>
                          <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="ruleMaxSaleQty">Max Sale Qty</Form.Label>
                        </Col>
                         <Col xs={6} sm={6} md={2} lg={2}>
                          <Form.Group>
                            <Form.Control id="ruleMaxOverMatch" value={values.rule?.MaxOverMatch} onChange={handleChange} isInvalid={errors.rule?.MaxOverMatch} isValid={values.rule?.MaxOverMatch && !errors.rule?.MaxOverMatch} disabled aria-label="Rule Max Over Match" />
                            <Form.Control.Feedback type='invalid'>{errors.rule?.MaxOverMatch}</Form.Control.Feedback>
                          </Form.Group>
                          <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="ruleMaxOverMatch">Max Over Match</Form.Label>
                        </Col>
                         <Col xs={6} sm={6} md={2} lg={2}>
                          <Form.Group>
                            <Form.Control id="ruleMaxUnderMatch" value={values.rule?.MaxUnderMatch} onChange={handleChange} isInvalid={errors.rule?.MaxUnderMatch} isValid={values.rule?.MaxUnderMatch && !errors.rule?.MaxUnderMatch} disabled aria-label="Rule Max Under Match" />
                            <Form.Control.Feedback type='invalid'>{errors.rule?.MaxUnderMatch}</Form.Control.Feedback>
                          </Form.Group>
                          <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="ruleMaxUnderMatch">Max Under Match</Form.Label>
                        </Col>
                      </Row>

                      <Row>
                        <formik.FieldArray name="newAssets">
                          {({ push, remove }) => (
                            <>
                              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
                                <b>New Asset(s)</b>
                                {(!values.rule?.MaxPurchaseQty || ((values?.newAssets?.length ?? -1) < (values.rule?.MaxPurchaseQty ?? 0))) && (
                                  <Button
                                    style={{ marginLeft: 20, }}
                                    onClick={() => {
                                      setSelectionModalChild(<Assets lockedAssetOwnerTypeFilter={[3]} itemSelected={(item) => {
                                        console.log(item);
                                        push({ newAssetId: item.Id, newAssetVin: item.Vin, newAssetClass: item?.AssetClass?.Name, newAssetYear: item.Year, newAssetMake: item.Make, newAssetModel: item.Model, newAssetValue: item.AdjustedCapitalCost?.toFixed(2) });
                                        setShowDataSelectionModal(false);
                                      }} />)
                                      setShowDataSelectionModal(true);
                                    }}
                                  >+ Add Asset</Button>)}
                              </div>
                              <br /><br />
                              {typeof errors?.newAssets === 'string' && (
                                <p style={{ color: 'red' }}>{errors.newAssets}</p>
                              )}
                              {values?.newAssets?.map((_, index) => (
                                <Row key={index} className='mb-3'>
                                  <Col xs={6} sm={6} md={3} lg={3}>
                                    <Form.Group>
                                      <Form.Control name={`newAsset.${index}.newAssetId`} value={values.newAssets?.[index]?.newAssetId} onChange={handleChange} isInvalid={errors.newAssets?.[index]?.newAssetId} isValid={values.newAssets?.[index]?.newAssetId && !errors.newAssets?.[index]?.newAssetId} disabled aria-label="New Asset Id" />
                                      <Form.Control.Feedback type='invalid'>{errors.newAsset?.[index]?.newAssetId}</Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor={`newAssets.${index}.newAssetId`}><span style={{ color: 'red' }}>*</span>Id</Form.Label>
                                  </Col>
                                  <Col xs={6} sm={6} md={3} lg={3}>
                                    <Form.Group>
                                      <Form.Control name={`newAssets.${index}.newAssetVin`} value={values.newAssets?.[index]?.newAssetVin} onChange={handleChange} isInvalid={errors.newAssets?.[index]?.newAssetVin} isValid={values.newAssets?.[index]?.newAssetVin && !errors.newAssets?.[index]?.newAssetVin} disabled aria-label="New Asset VIN" />
                                      <Form.Control.Feedback type='invalid'>{errors.newAssets?.[index]?.newAssetVin}</Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor={`newAssets.${index}.newAssetVin`}><span style={{ color: 'red' }}>*</span>VIN</Form.Label>
                                  </Col>
                                  <Col xs={6} sm={6} md={3} lg={3}>
                                    <Form.Group>
                                      <Form.Control name={`newAssets.${index}.newAssetClass`} value={values.newAssets?.[index]?.newAssetClass} onChange={handleChange} isInvalid={errors.newAssets?.[index]?.newAssetClass} isValid={values.newAssets?.[index]?.newAssetClass && !errors.newAssets?.[index]?.newAssetClass} disabled aria-label="New Asset Class" />
                                      <Form.Control.Feedback type='invalid'>{errors.newAssets?.[index]?.newAssetClass}</Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor={`newAssets.${index}.newAssetClass`}><span style={{ color: 'red' }}>*</span>Class</Form.Label>
                                  </Col>
                                  <Col xs={6} sm={6} md={3} lg={3}>
                                    <Form.Group>
                                      <Form.Control name={`newAssets.${index}.newAssetYear`} value={values.newAssets?.[index]?.newAssetYear} onChange={handleChange} isInvalid={errors.newAssets?.[index]?.newAssetYear} isValid={values.newAssets?.[index]?.newAssetYear && !errors.newAssets?.[index]?.newAssetYear} disabled aria-label="New Asset Year" />
                                      <Form.Control.Feedback type='invalid'>{errors.newAssets?.[index]?.newAssetYear}</Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor={`newAssets.${index}.newAssetYear`}><span style={{ color: 'red' }}>*</span>Year</Form.Label>
                                  </Col>
                                  <Col xs={6} sm={6} md={3} lg={3}>
                                    <Form.Group>
                                      <Form.Control name={`newAssets.${index}.newAssetMake`} value={values.newAssets?.[index]?.newAssetMake} onChange={handleChange} isInvalid={errors.newAssets?.[index]?.newAssetMake} isValid={values.newAssets?.[index]?.newAssetMake && !errors.newAssets?.[index]?.newAssetMake} disabled aria-label="New Asset Make" />
                                      <Form.Control.Feedback type='invalid'>{errors.newAssets?.[index]?.newAssetMake}</Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor={`newAssets.${index}.newAssetMake`}><span style={{ color: 'red' }}>*</span>Make</Form.Label>
                                  </Col>
                                  <Col xs={6} sm={6} md={3} lg={3}>
                                    <Form.Group>
                                      <Form.Control name={`newAssets.${index}.newAssetModel`} value={values.newAssets?.[index]?.newAssetModel} onChange={handleChange} isInvalid={errors.newAssets?.[index]?.newAssetModel} isValid={values.newAssets?.[index]?.newAssetModel && !errors.newAssets?.[index]?.newAssetModel} disabled aria-label="New Asset Model" />
                                      <Form.Control.Feedback type='invalid'>{errors.newAssets?.[index]?.newAssetModel}</Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor={`newAssets.${index}.newAssetModel`}><span style={{ color: 'red' }}>*</span>Model</Form.Label>
                                  </Col>
                                  <Col xs={6} sm={6} md={3} lg={3}>
                                    <Form.Group>
                                      <Form.Control name={`newAssets.${index}.newAssetValue`} value={values.newAssets?.[index]?.newAssetValue} onChange={handleChange} isInvalid={errors.newAssets?.[index]?.newAssetValue} isValid={values.newAssets?.[index]?.newAssetValue && !errors.newAssets?.[index]?.newAssetValue} disabled aria-label="New Asset Value" />
                                      <Form.Control.Feedback type='invalid'>{errors.newAssets?.[index]?.newAssetValue}</Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor={`newAssets.${index}.newAssetValue`}><span style={{ color: 'red' }}>*</span>Value</Form.Label>
                                  </Col>
                                  <Col xs={6} sm={6} md={3} lg={3}>
                                    <Form.Group>
                                      <Form.Control name={`newAssets.${index}.newAssetAdjustedCapCost`} value={values.newAssets?.[index]?.newAssetAdjustedCapCost} onChange={handleChange} isInvalid={errors.newAssets?.[index]?.newAssetAdjustedCapCost} isValid={values.newAsset?.[index]?.newAssetAdjustedCapCost && !errors.newAssets?.[index]?.newAssetAdjustedCapCost} aria-label="New Asset Adjusted Cap Cost" />
                                      <Form.Control.Feedback type='invalid'>{errors.newAssets?.[index]?.newAssetAdjustedCapCost}</Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor={`newAssets.${index}.newAssetAdjustedCapCost`}><span style={{ color: 'red' }}>*</span>Adjusted Cap Cost</Form.Label>
                                    <InformationTooltip text='Cost before local fees' style={{ marginTop: 10 }} />
                                  </Col>
                                  <Col xs={12} sm={12} md={12} lg={12} className="">
                                    <Button
                                      variant='danger'
                                      style={{ width: '100%' }}
                                      onClick={() => remove(index)}
                                    >Remove</Button>
                                  </Col>
                                </Row>
                              ))}
                            </>
                          )}
                        </formik.FieldArray>
                      </Row>
                      <Row>
                        <formik.FieldArray name="retiredAssets">
                          {({ push, remove }) => (
                            <>
                              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
                                <b>Retired Assets(s)</b>
                                {(!values.rule?.MaxSaleQty || ((values?.retiredAssets?.length ?? -1) < (values.rule?.MaxSaleQty ?? 0))) && (
                                  <Button
                                    style={{ marginLeft: 20, }}
                                    onClick={() => {
                                      setSelectionModalChild(<Assets lockedAssetOwnerFilter={[selectedDealer]} itemSelected={(item) => {

                                        push({ retiredAssetId: item.Id, retiredAssetVin: item.Vin, retiredAssetClass: item?.AssetClass?.Name, retiredAssetYear: item.Year, retiredAssetMake: item.Make, retiredAssetModel: item.Model, retiredAssetValue: item.Credit, retiredAssetAdjustedCapCost: item.Credit?.toFixed(2) });
                                        setShowDataSelectionModal(false);
                                      }} />)
                                      setShowDataSelectionModal(true);
                                    }}
                                  >+ Add Retired Asset</Button>)}
                              </div>
                              <br /><br />
                              {typeof errors?.retiredAssets === 'string' && (
                                <p style={{ color: 'red' }}>{errors.retiredAssets}</p>
                              )}
                              {values?.retiredAssets?.map((_, index) => (
                                <Row key={index}>
                                  <Col xs={6} sm={6} md={3} lg={3}>
                                    <Form.Group>
                                      <Form.Control name={`retiredAsset.${index}.retiredAssetId`} value={values.retiredAssets?.[index]?.retiredAssetId} onChange={handleChange} isInvalid={errors.retiredAssets?.[index]?.retiredAssetId} isValid={values.retiredAssets?.[index]?.retiredAssetId && !errors.retiredAssets?.[index]?.retiredAssetId} disabled aria-label="Trade In Asset Id" />
                                      <Form.Control.Feedback type='invalid'>{errors.retiredAsset?.[index]?.retiredAssetId}</Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor={`retiredAssets.${index}.retiredAssetId`}><span style={{ color: 'red' }}>*</span>Id</Form.Label>
                                  </Col>
                                  <Col xs={6} sm={6} md={3} lg={3}>
                                    <Form.Group>
                                      <Form.Control name={`retiredAssets.${index}.retiredAssetVin`} value={values.retiredAssets?.[index]?.retiredAssetVin} onChange={handleChange} isInvalid={errors.retiredAssets?.[index]?.retiredAssetVin} isValid={values.retiredAssets?.[index]?.retiredAssetVin && !errors.retiredAssets?.[index]?.retiredAssetVin} disabled aria-label="Lease Asset VIN" />
                                      <Form.Control.Feedback type='invalid'>{errors.retiredAssets?.[index]?.retiredAssetVin}</Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor={`retiredAssets.${index}.retiredAssetVin`}><span style={{ color: 'red' }}>*</span>VIN</Form.Label>
                                  </Col>
                                  <Col xs={6} sm={6} md={3} lg={3}>
                                    <Form.Group>
                                      <Form.Control name={`retiredAssets.${index}.retiredAssetClass`} value={values.retiredAssets?.[index]?.retiredAssetClass} onChange={handleChange} isInvalid={errors.retiredAssets?.[index]?.retiredAssetClass} isValid={values.retiredAssets?.[index]?.retiredAssetClass && !errors.retiredAssets?.[index]?.retiredAssetClass} disabled aria-label="Lease Asset Class" />
                                      <Form.Control.Feedback type='invalid'>{errors.retiredAssets?.[index]?.retiredAssetClass}</Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor={`retiredAssets.${index}.retiredAssetClass`}><span style={{ color: 'red' }}>*</span>Class</Form.Label>
                                  </Col>
                                  <Col xs={6} sm={6} md={3} lg={3}>
                                    <Form.Group>
                                      <Form.Control name={`retiredAssets.${index}.retiredAssetYear`} value={values.retiredAssets?.[index]?.retiredAssetYear} onChange={handleChange} isInvalid={errors.retiredAssets?.[index]?.retiredAssetYear} isValid={values.retiredAssets?.[index]?.retiredAssetYear && !errors.retiredAssets?.[index]?.retiredAssetYear} disabled aria-label="Lease Asset Year" />
                                      <Form.Control.Feedback type='invalid'>{errors.retiredAssets?.[index]?.retiredAssetYear}</Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor={`retiredAssets.${index}.retiredAssetYear`}><span style={{ color: 'red' }}>*</span>Year</Form.Label>
                                  </Col>
                                  <Col xs={6} sm={6} md={3} lg={3}>
                                    <Form.Group>
                                      <Form.Control name={`retiredAssets.${index}.retiredAssetMake`} value={values.retiredAssets?.[index]?.retiredAssetMake} onChange={handleChange} isInvalid={errors.retiredAssets?.[index]?.retiredAssetMake} isValid={values.retiredAssets?.[index]?.retiredAssetMake && !errors.retiredAssets?.[index]?.retiredAssetMake} disabled aria-label="Lease Asset Make" />
                                      <Form.Control.Feedback type='invalid'>{errors.retiredAssets?.[index]?.retiredAssetMake}</Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor={`retiredAssets.${index}.retiredAssetMake`}><span style={{ color: 'red' }}>*</span>Make</Form.Label>
                                  </Col>
                                  <Col xs={6} sm={6} md={3} lg={3}>
                                    <Form.Group>
                                      <Form.Control name={`retiredAssets.${index}.retiredAssetModel`} value={values.retiredAssets?.[index]?.retiredAssetModel} onChange={handleChange} isInvalid={errors.retiredAssets?.[index]?.retiredAssetModel} isValid={values.retiredAssets?.[index]?.retiredAssetModel && !errors.retiredAssets?.[index]?.retiredAssetModel} disabled aria-label="Lease Asset Model" />
                                      <Form.Control.Feedback type='invalid'>{errors.retiredAssets?.[index]?.retiredAssetModel}</Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor={`retiredAssets.${index}.retiredAssetModel`}><span style={{ color: 'red' }}>*</span>Model</Form.Label>
                                  </Col>
                                  <Col xs={6} sm={6} md={3} lg={3}>
                                    <Form.Group>
                                      <Form.Control name={`retiredAssets.${index}.retiredAssetValue`} value={values.retiredAssets?.[index]?.retiredAssetValue} onChange={handleChange} isInvalid={errors.retiredAssets?.[index]?.retiredAssetValue} isValid={values.retiredAssets?.[index]?.retiredAssetValue && !errors.retiredAssets?.[index]?.retiredAssetValue} disabled aria-label="Lease Asset Credits" />
                                      <Form.Control.Feedback type='invalid'>{errors.retiredAssets?.[index]?.retiredAssetValue}</Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor={`retiredAssets.${index}.retiredAssetValue`}><span style={{ color: 'red' }}>*</span>Credits</Form.Label>
                                  </Col>
                                  <Col xs={6} sm={6} md={3} lg={3}>
                                    <Form.Group>
                                      <Form.Control name={`retiredAssets.${index}.retiredAssetAdjustedCapCost`} value={values.retiredAssets?.[index]?.retiredAssetAdjustedCapCost} onChange={handleChange} isInvalid={errors.retiredAssets?.[index]?.retiredAssetAdjustedCapCost} isValid={values.retiredAssets?.[index]?.retiredAssetAdjustedCapCost && !errors.retiredAssets?.[index]?.retiredAssetAdjustedCapCost} aria-label="Adjusted Cap Cost" />
                                      <Form.Control.Feedback type='invalid'>{errors.retiredAssets?.[index]?.retiredAssetAdjustedCapCost}</Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor={`retiredAssets.${index}.retiredAssetAdjustedCapCost`}><span style={{ color: 'red' }}>*</span>Adjusted Cap Cost</Form.Label>
                                    <InformationTooltip text='Cost before local fees' style={{ marginTop: 10 }} />
                                  </Col>
                                  <Col xs={12} sm={12} md={12} lg={12} className="">
                                    <Button
                                      variant='danger'
                                      style={{ width: '100%' }}
                                      onClick={() => remove(index)}
                                    >Remove</Button>
                                  </Col>
                                </Row>
                              ))}
                            </>
                          )}
                        </formik.FieldArray>
                      </Row>

                      <Row>
                        <Col className="d-flex justify-content-end">
                          <Button style={{ marginTop: 20 }} type="submit">
                            Find Matches
                          </Button>
                        </Col>
                      </Row>
                    </>
                  )}


                  <SeparatorLine />
                  <br />
                  {
                    resultsLoading ? <Spinner animation="border" role="status" /> : (matches?.Matches?.length ?? 0) === 0 ? 'no data' : (
                      <div>
                        <Header title="Results" description={"Compare packages for " + data?.newAssets?.length + ' vehicle(s)'} />
                        {matches?.Matches?.map((d, idx) => (
                          <Card key={idx}>
                            <Card.Body>
                              <Row className="align-items-stretch">

                                <Col xs={12} sm={12} md={12} lg={7}>
                                  <div className='h-100'>
                                    {/* Tags at the top */}
                                    <Stack direction="horizontal" gap={2} className="mb-2">
                                      {d?.Tags?.map((a, idx) => (
                                        <Badge key={idx} bg="success">{a}</Badge>
                                      ))}
                                    </Stack>

                                    {d?.RetiredAssets?.map((a, idx) => (
                                      <Row key={idx} className="align-items-center mb-3 py-2 border-bottom">
                                        {/* Checkbox column - shrinks on small screens, fixed on medium+ */}
                                        <Col xs="auto" className="pe-0">
                                          <Form.Check
                                            type="checkbox"
                                            checked={!!values.retiredAssets.find(c => c.retiredAssetId === a.Id)}
                                            onChange={(event) => {
                                              if (event.target.checked) {
                                                if (!values.retiredAssets.find(c => c.retiredAssetId === a.Id)) {
                                                  values.retiredAssets.push({
                                                    retiredAssetId: a.Id,
                                                    retiredAssetVin: a.Vin,
                                                    retiredAssetClass: a?.AssetClass?.Name,
                                                    retiredAssetYear: a.Year,
                                                    retiredAssetMake: a.Make,
                                                    retiredAssetModel: a.Model,
                                                    retiredAssetValue: a.Credit,
                                                    retiredAssetAdjustedCapCost: a.Credit?.toFixed(2)
                                                  });
                                                }
                                              } else {
                                                values.retiredAssets = values.retiredAssets.filter(c => c.retiredAssetId !== a.Id);
                                              }
                                              setFieldValue('retiredAssets', values.retiredAssets);
                                            }}
                                          />
                                        </Col>

                                        {/* Vehicle details column - stacks on small screens, expands on medium+ */}
                                        <Col xs={11} sm={11} md={11} lg={11}>
                                          <div className="d-flex align-items-center">
                                            <FontAwesomeIcon icon={faCar} className="me-2" />
                                            <span>
                                              <p className='mt-0 mb-0 text-muted'>{a.Year} {a.Make} {a.Model} {a.Color} {a.BodyStyle}</p>
                                              <p className="mt-0 mb-0">{a.Vin}</p>
                                              <p className="mt-0 mb-0">Credits: {formatCurrency(a.Credit)}</p>
                                              <p className='mt-0 mb-0 text-muted'>Expires: {formatDateToMMDDYYYY(a.CreditExpiryDate)}</p>
                                            </span>
                                          </div>
                                        </Col>
                                      </Row>
                                    ))}
                                  </div>
                                </Col>

                                {/* Right Column for summary and button - takes 12 cols on small, 4 on medium+ */}
                                <Col xs={12} sm={12} md={12} lg={5} className="d-flex" style={{ paddingBottom: 12 }}>
                                  <Card className="w-100 h-100">
                                    <Card.Body className="d-flex flex-column">
                                      <div>
                                        <div className="d-flex justify-content-between align-items-center mt-3">
                                          <span className='text-end'>Total Adjusted Capital Costs:</span>
                                          <span className="text-end" style={{ minWidth: '130px' }}>
                                            {formatCurrency(d.AdjustedCapitalCost)}
                                          </span>
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center mt-3">
                                          <span>Total credits:</span>
                                          <span className="text-end" style={{ minWidth: '130px' }}>
                                            {formatCurrency(d.TotalCredits)}
                                          </span>
                                        </div>
                                      </div>

                                      <div className="mt-auto">
                                        <hr />
                                        <div className="d-flex justify-content-between align-items-center mt-3" style={{ fontWeight: 'bold' }}>
                                          <span>Outstanding tax due:</span>
                                          <span className="text-end" style={{ minWidth: '130px', color: 'black' }}>
                                            {d?.OutstandingTaxableCredits < 0 ? '$0' : formatCurrency(d.OutstandingTaxableCredits)}
                                          </span>
                                        </div>

                                        {d?.OutstandingTaxableCredits < 0 && (
                                          <div className="d-flex justify-content-between align-items-center mt-3" style={{ fontWeight: 'bold' }}>
                                            <span>Credit Loss:</span>
                                            <span className="text-end" style={{ minWidth: '130px', color: 'red' }}>
                                              {formatCurrency(d.OutstandingTaxableCredits)}
                                            </span>
                                          </div>
                                        )}
                                        <br />
                                        <Button variant="primary" className="w-100" onClick={() => handleCreateTransaction(d)}>
                                          Create Transaction
                                        </Button>
                                      </div>
                                    </Card.Body>
                                  </Card>
                                </Col>
                              </Row>
                            </Card.Body>
                          </Card>
                        ))}

                      </div>
                    )
                  }
                </Form>
              )}
            </Formik>
          </>
        )}

        <DataSelectionModal
          show={showDataSelectionModal}
          onHide={() => { setShowDataSelectionModal(false); }}
          dataType='Asset'
          child={selectionModalChild}
        />

        <CreateEditTransactionModal
          show={showCreateTransactionModal}
          match={match}
          onHide={(bubble) => {
            setShowCreateTransactionModal(false)
            // if (bubble)

          }}
        />
      </>
    </div >
  );
};

export default Discover;
